<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Update Cost Center"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @save="save"
    @dismiss="dismiss"
  >
    <CostCenterField
      v-model="costCenter"
      :hub-id="hubId"
      required
      data-test="bulk-update-cost-center"
    />

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update cost center
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import CostCenterField from '../common/CostCenterField.vue'
import { computed, ref, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { bulkUpdateCostCenter } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)
const costCenter = ref<string | undefined>()
let initialCostCenter: string | undefined

onMounted(() => {
  const selectedCostCenters: Set<string> = new Set(props.selectedJobs.map((job) => job.cost_center))
  if (selectedCostCenters.size === 1) {
    initialCostCenter = [...selectedCostCenters][0]
  }
  costCenter.value = initialCostCenter
})

const hubId = computed<string>(() => {
  return props.selectedJobs[0].hub_id
})
const hasChanges = computed<boolean>(() => {
  return costCenter.value !== initialCostCenter
})
const showHint = computed<boolean>(() => {
  return !costCenter.value && initialCostCenter === undefined
})

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')

const save = async () => {
  saving.value = true
  try {
    await bulkUpdateCostCenter(
      props.selectedJobs.map((job) => job.number),
      costCenter.value,
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { cost_center: costCenter.value }),
    )
    emits('updated', { message: 'Cost center updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
